import React, { useState } from 'react'
import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  Text,
  Button,
  Avatar,
  Badge,
  Card,
  CardBody,
  CardHeader,
  VStack,
  HStack,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatGroup,
  useDisclosure,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react'
import { useQuery } from 'react-query'
import { EditIcon, SettingsIcon } from '@chakra-ui/icons'
import { api } from '../services/api'
import ProfileEditor from '../components/ProfileEditor'
import PreferencesPanel from '../components/PreferencesPanel'
import UserStatsCard from '../components/UserStatsCard'

interface UserProfile {
  const_id: string
  username?: string
  email?: string
  name?: string
  picture_url?: string
  nickname?: string
  bio?: string
  location?: string
  website?: string
  company?: string
  job_title?: string
  programming_languages?: string[]
  experience_level?: string
  linkedin_url?: string
  github_url?: string
  twitter_url?: string
  profile_visibility?: string
  show_real_name?: boolean
  show_email?: boolean
  show_location?: boolean
  show_stats?: boolean
  time_created?: string
  time_updated?: string
}

interface UserStats {
  total_submissions: number
  successful_submissions: number
  problems_solved: number
  total_points: number
  current_rank?: number
  average_runtime?: number
  best_runtime?: number
  fastest_problem?: string
  days_active: number
  longest_streak: number
  current_streak: number
  last_submission?: string
  languages_used?: Record<string, number>
  achievements?: string[]
  recent_problems?: Array<{
    const_id: string
    title: string
    status: string
    time_created: string
  }>
}

const UserProfile: React.FC = () => {
  const [editingProfile, setEditingProfile] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const toast = useToast()

  const { 
    data: profile, 
    isLoading: profileLoading, 
    error: profileError,
    refetch: refetchProfile 
  } = useQuery<UserProfile>('userProfile', () => 
    api.get('/user/profile').then(res => res.data)
  )

  const { 
    data: stats, 
    isLoading: statsLoading, 
    error: statsError 
  } = useQuery<UserStats>('userStats', () => 
    api.get('/user/stats').then(res => res.data)
  )

  const handleProfileUpdate = () => {
    refetchProfile()
    setEditingProfile(false)
    toast({
      title: 'Profile updated',
      description: 'Your profile has been successfully updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }

  if (profileLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading your profile...</Text>
        </VStack>
      </Container>
    )
  }

  if (profileError) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading profile!</AlertTitle>
          <AlertDescription>
            We couldn't load your profile. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </Container>
    )
  }

  if (!profile) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert status="warning">
          <AlertIcon />
          <AlertTitle>No profile found!</AlertTitle>
          <AlertDescription>
            We couldn't find your profile. Please contact support.
          </AlertDescription>
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxW="container.xl" py={8}>
      <Grid templateColumns={{ base: '1fr', lg: '1fr 300px' }} gap={8}>
        {/* Main Profile Content */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Profile Header */}
            <Card>
              <CardBody>
                <HStack spacing={6} align="start">
                  <Avatar
                    size="2xl"
                    name={profile.name || profile.username || 'User'}
                    src={profile.picture_url || ''}
                  />
                  <VStack align="start" flex={1} spacing={3}>
                    <VStack align="start" spacing={1}>
                      <Heading size="lg">
                        {profile.show_real_name && profile.name ? profile.name : profile.username}
                      </Heading>
                      {profile.nickname && (
                        <Text color="gray.600" fontSize="md">@{profile.nickname}</Text>
                      )}
                      {profile.job_title && profile.company && (
                        <Text color="gray.600">
                          {profile.job_title} at {profile.company}
                        </Text>
                      )}
                      {profile.location && profile.show_location && (
                        <Text color="gray.500" fontSize="sm">📍 {profile.location}</Text>
                      )}
                    </VStack>
                    
                    {profile.bio && (
                      <Text color="gray.700" mt={2}>
                        {profile.bio}
                      </Text>
                    )}

                    {profile.programming_languages && profile.programming_languages.length > 0 && (
                      <HStack spacing={2} flexWrap="wrap">
                        {profile.programming_languages.map((lang) => (
                          <Badge key={lang} colorScheme="blue" variant="subtle">
                            {lang}
                          </Badge>
                        ))}
                      </HStack>
                    )}

                    <HStack spacing={3} mt={4}>
                      <Button
                        leftIcon={<EditIcon />}
                        colorScheme="blue"
                        onClick={() => setEditingProfile(true)}
                      >
                        Edit Profile
                      </Button>
                      <Button
                        leftIcon={<SettingsIcon />}
                        variant="outline"
                        onClick={() => setShowPreferences(true)}
                      >
                        Preferences
                      </Button>
                    </HStack>
                  </VStack>
                </HStack>
              </CardBody>
            </Card>

            {/* Statistics Section */}
            {stats && !statsLoading && (
              <UserStatsCard stats={stats} />
            )}

            {/* Recent Activity */}
            {stats?.recent_problems && stats.recent_problems.length > 0 && (
              <Card>
                <CardHeader>
                  <Heading size="md">Recent Activity</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={3} align="stretch">
                    {stats.recent_problems.map((problem, index) => (
                      <HStack key={index} spacing={4} p={3} borderRadius="md" bg="gray.50">
                        <VStack align="start" flex={1} spacing={1}>
                          <Text fontWeight="medium">{problem.title}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {new Date(problem.time_created).toLocaleDateString()}
                          </Text>
                        </VStack>
                        <Badge
                          colorScheme={problem.status === 'success' ? 'green' : 'red'}
                          variant="subtle"
                        >
                          {problem.status}
                        </Badge>
                      </HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </GridItem>

        {/* Sidebar */}
        <GridItem>
          <VStack spacing={6} align="stretch">
            {/* Quick Stats */}
            {stats && !statsLoading && (
              <Card>
                <CardHeader>
                  <Heading size="sm">Quick Stats</Heading>
                </CardHeader>
                <CardBody>
                  <StatGroup>
                    <Stat>
                      <StatLabel>Rank</StatLabel>
                      <StatNumber>#{stats.current_rank || 'N/A'}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Points</StatLabel>
                      <StatNumber>{stats.total_points}</StatNumber>
                    </Stat>
                  </StatGroup>
                  <Divider my={3} />
                  <StatGroup>
                    <Stat>
                      <StatLabel>Solved</StatLabel>
                      <StatNumber>{stats.problems_solved}</StatNumber>
                    </Stat>
                    <Stat>
                      <StatLabel>Streak</StatLabel>
                      <StatNumber>{stats.current_streak}</StatNumber>
                      <StatHelpText>days</StatHelpText>
                    </Stat>
                  </StatGroup>
                </CardBody>
              </Card>
            )}

            {/* Achievements */}
            {stats?.achievements && stats.achievements.length > 0 && (
              <Card>
                <CardHeader>
                  <Heading size="sm">Achievements</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing={2} align="stretch">
                    {stats.achievements.map((achievement, index) => (
                      <HStack key={index} spacing={3}>
                        <Text fontSize="lg">🏆</Text>
                        <Text fontSize="sm">{achievement}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </CardBody>
              </Card>
            )}
          </VStack>
        </GridItem>
      </Grid>

      {/* Profile Editor Modal */}
      <ProfileEditor
        isOpen={editingProfile}
        onClose={() => setEditingProfile(false)}
        profile={profile}
        onUpdate={handleProfileUpdate}
      />

      {/* Preferences Panel Modal */}
      <PreferencesPanel
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
      />
    </Container>
  )
}

export default UserProfile 